import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AuthService } from '../../services/auth.service';
import { TwoFactorAuth, TwoFactorSetupData } from '../../models/two-factor.model';
import { authenticator } from 'otplib';
import * as QRCode from 'qrcode';
import * as CryptoJS from 'crypto-js';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TwoFactorAuthService {
  private readonly encryptionKey = 'DomoBlockSecureKey2FA'; // En producción, esto debería estar en environment.ts o mejor aún, en server-side
  private readonly collectionName = 'two-factor-auth'; // Nombre de la colección separada para 2FA

  constructor(
    private afs: AngularFirestore,
    private authService: AuthService,
    private http: HttpClient
  ) {}

  /**
   * Genera un nuevo secreto para el usuario y la URL del código QR
   */
  async generateSecret(uid: string, email: string): Promise<TwoFactorSetupData> {
    if (!uid || !email) {
      console.error('UID o email no proporcionados');
      throw new Error('Se requiere un UID y email válidos para generar el secreto');
    }

    const secret = authenticator.generateSecret();
    const appName = 'Domoblock';
    const otpAuthUrl = authenticator.keyuri(email, appName, secret);
    
    return new Promise((resolve, reject) => {
      QRCode.toDataURL(otpAuthUrl, (err, qrCodeUrl) => {
        if (err) {
          console.error('Error al generar código QR:', err);
          reject(err);
          return;
        }
        resolve({ secret, qrCodeUrl });
      });
    });
  }

  /**
   * Verifica el código TOTP ingresado por el usuario usando el Backend
   */
  async verifyTokenAPI(code: string): Promise<boolean> {
    if (!code) {
      return false;
    }
    
    try {
      let token: string | undefined;
      try {
        const authData = sessionStorage.getItem('pending2FAAuth');
        if (authData) {
          const parsed = JSON.parse(authData);
          if (parsed?.initialToken) {
            token = parsed.initialToken;
          }
        }
      } catch (e) {}
      
      if (!token) {
        token = await this.authService.getJWTToken();
      }
      if (!token) {
        return false;
      }

      const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
      const body = { code };
      
      const response = await this.http.post<any>(`${environment.api.url}/auth/mfa/verify`, body, { headers }).toPromise();

      if (response?.success === true) {
        // Refrescar y almacenar el token con las nuevas claims mfa:true
        await this.authService.storeMFAToken();
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Confirma el setup 2FA en backend: verifica el código contra el secret server-side,
   * activa 2FA y crea mfa_session fresca atómicamente.
   */
  async confirmSetupAPI(secret: string, code: string): Promise<string[]> {
    const token = await this.authService.getJWTToken();
    if (!token) throw new Error('No hay sesión activa');

    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    const response = await this.http.post<{ backupCodes: string[] }>(
      `${environment.api.url}/auth/2fa/setup/confirm`,
      { secret, code },
      { headers }
    ).toPromise();

    return response?.backupCodes || [];
  }

  /**
   * Crea un challenge 2FA para el proceso de login
   */
  async createChallengeAPI(): Promise<{ challengeId: string; expiresAt: number; serverTime: number; period: number } | null> {
    try {
      const token = await this.authService.getJWTToken();
      if (!token) return null;

      const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
      const response = await this.http.post<any>(`${environment.api.url}/auth/2fa/challenge`, {}, { headers }).toPromise();
      return response;
    } catch (error) {
      console.error('Error creando challenge 2FA:', error);
      return null;
    }
  }

  /**
   * Verifica un challenge 2FA durante el proceso de login
   */
  async verifyChallengeAPI(challengeId: string, code: string): Promise<boolean> {
    try {
      const token = await this.authService.getJWTToken();
      if (!token) return false;

      const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
      const body = { challengeId, code };
      
      const response = await this.http.post<any>(`${environment.api.url}/auth/2fa/verify`, body, { headers }).toPromise();
      return response?.success === true;
    } catch (error) {
      console.error('Error verificando challenge 2FA:', error);
      return false;
    }
  }

  /**
   * Verifica el código TOTP ingresado por el usuario (Lado-Cliente, Inseguro/Legado)
   * @deprecated Usar verifyTokenAPI
   */
  verifyToken(token: string, secret: string): boolean {
    if (!token || !secret) {
      console.error('Token o secreto no proporcionados');
      return false;
    }
    
    try {
      const decryptedSecret = this.decryptSecret(secret);
      return authenticator.verify({ token, secret: decryptedSecret });
    } catch (error) {
      console.error('Error al verificar token:', error);
      return false;
    }
  }

  /**
   * Cifra el secreto TOTP antes de guardarlo
   */
  private encryptSecret(secret: string): string {
    return CryptoJS.AES.encrypt(secret, this.encryptionKey).toString();
  }

  /**
   * Descifra el secreto TOTP para verificación
   */
  private decryptSecret(encryptedSecret: string): string {
    const bytes = CryptoJS.AES.decrypt(encryptedSecret, this.encryptionKey);
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  /**
   * Genera códigos de respaldo aleatorios
   */
  private generateBackupCodes(): string[] {
    return Array(8).fill(0).map(() => 
      Math.random().toString(36).substring(2, 8).toUpperCase()
    );
  }

  /**
   * Habilita 2FA para el usuario
   */
  async enable2FA(uid: string, secret: string): Promise<string[]> {
    if (!uid) {
      console.error('UID no proporcionado para enable2FA');
      throw new Error('Se requiere un UID válido para habilitar 2FA');
    }

    if (!secret) {
      console.error('Secreto no proporcionado para enable2FA');
      throw new Error('Se requiere un secreto válido para habilitar 2FA');
    }

    console.log(`Iniciando habilitación de 2FA para UID: ${uid}`);
    
    try {
      const encryptedSecret = this.encryptSecret(secret);
      const backupCodes = this.generateBackupCodes();
      
      // Usar la colección separada para 2FA con el UID como ID del documento
      await this.afs.collection(this.collectionName).doc(uid).set({
        enabled: true,
        secret: encryptedSecret,
        backupCodes: backupCodes,
        lastUsed: new Date(),
        userId: uid, // Referencia al usuario para facilitar consultas
        createdAt: new Date()
      });
      
      console.log('2FA habilitado exitosamente');
      return backupCodes;
    } catch (error) {
      console.error('Error al habilitar 2FA:', error);
      throw error;
    }
  }

  /**
   * Deshabilita 2FA para el usuario
   */
  async disable2FA(uid: string): Promise<void> {
    if (!uid) {
      console.error('UID no proporcionado para disable2FA');
      throw new Error('Se requiere un UID válido para deshabilitar 2FA');
    }

    try {
      // Eliminar el documento de 2FA completamente
      await this.afs.collection(this.collectionName).doc(uid).delete();
      console.log('2FA deshabilitado exitosamente');
    } catch (error) {
      console.error('Error al deshabilitar 2FA:', error);
      throw error;
    }
  }

  /**
   * Verifica si el usuario tiene 2FA habilitado
   */
  async isEnabled(uid: string): Promise<boolean> {
    if (!uid) {
      console.error('UID no proporcionado para isEnabled');
      return false;
    }

    try {
      const doc = await this.afs.collection(this.collectionName).doc(uid).get().toPromise();
      if (!doc?.exists) {
        return false;
      }
      const data = doc.data() as {enabled?: boolean};
      return data.enabled === true;
    } catch (error) {
      console.error('Error al verificar estado 2FA:', error);
      return false;
    }
  }

  /**
   * Obtiene los datos de 2FA del usuario
   */
  async getTwoFactorData(uid: string): Promise<TwoFactorAuth | null> {
    if (!uid) {
      console.error('UID no proporcionado para getTwoFactorData');
      return null;
    }

    try {
      const doc = await this.afs.collection(this.collectionName).doc(uid).get().toPromise();
      if (!doc?.exists) {
        console.warn(`No se encontró configuración 2FA para el UID: ${uid}`);
        return null;
      }
      
      const data = doc.data() as any;
      if (data?.enabled) {
        return {
          enabled: data.enabled,
          secret: data.secret,
          backupCodes: data.backupCodes || [],
          lastUsed: data.lastUsed
        };
      }
    } catch (error) {
      console.error('Error al obtener datos 2FA:', error);
    }
    
    return null;
  }

  /**
   * Verifica si un código de respaldo es válido y lo consume
   */
  async verifyAndConsumeBackupCode(uid: string, code: string): Promise<boolean> {
    if (!uid || !code) {
      console.error('UID o código no proporcionados');
      return false;
    }

    try {
      const twoFactorData = await this.getTwoFactorData(uid);
      
      if (!twoFactorData || !twoFactorData.backupCodes || !twoFactorData.backupCodes.length) {
        return false;
      }
      
      const index = twoFactorData.backupCodes.indexOf(code);
      if (index === -1) {
        return false;
      }
      
      // Eliminar el código de respaldo usado
      const backupCodes = [...twoFactorData.backupCodes];
      backupCodes.splice(index, 1);
      
      // Actualizar la colección 2FA
      await this.afs.collection(this.collectionName).doc(uid).update({
        backupCodes: backupCodes,
        lastUsed: new Date()
      });
      
      return true;
    } catch (error) {
      console.error('Error al verificar código de respaldo:', error);
      return false;
    }
  }
}
